import { useState } from "react";
import { useIonToast } from "@ionic/react";

import { desuscribirPush, estadoPush, suscribirPush } from "src/services/pushNotifications";

export const usePushNotifications = () => {
  const [present] = useIonToast();
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState<NotificationPermission>("default");
  const [pushActive, setPushActive] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState("");

  const soportaPush = () => {
    return (
      typeof window !== "undefined"
      && "serviceWorker" in navigator
      && "PushManager" in window
      && "Notification" in window
    );
  };

  const urlBase64ToUint8Array = (base64String: string) => {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const output = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; i += 1) {
      output[i] = rawData.charCodeAt(i);
    }
    return output;
  };

  const obtenerSuscripcionPush = async () => {
    if (!soportaPush()) return null;
    const registro = await navigator.serviceWorker.ready;
    return registro.pushManager.getSubscription();
  };

  const refrescarEstadoPush = async () => {
    const supported = soportaPush();
    setPushSupported(supported);
    if (!supported) {
      setPushActive(false);
      return;
    }

    setPushPermission(Notification.permission);
    try {
      const sub = await obtenerSuscripcionPush();
      const backend = await estadoPush();
      const activoBackend = Boolean(backend?.data?.activo);
      setPushActive(Boolean(sub) && activoBackend);
    } catch {
      setPushActive(false);
    }
  };

  const activarNotificaciones = async () => {
    if (pushLoading) return;
    setPushError("");

    if (!soportaPush()) {
      present({ message: "Este dispositivo no soporta notificaciones push.", duration: 4000, color: "warning" });
      return;
    }

    if (Notification.permission === "denied") {
      setPushError("Permiso denegado. Habilitalo desde el navegador.");
      return;
    }

    setPushLoading(true);
    try {
      let permiso: NotificationPermission = Notification.permission;
      if (permiso === "default") {
        permiso = await Notification.requestPermission();
      }
      if (permiso !== "granted") {
        setPushError("No se otorgo el permiso para notificaciones.");
        return;
      }

      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";
      if (!vapidKey) {
        setPushError("Falta configurar la clave VAPID publica.");
        return;
      }

      const registro = await navigator.serviceWorker.ready;
      let sub = await registro.pushManager.getSubscription();
      if (!sub) {
        sub = await registro.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }

      const plataforma = /android/i.test(navigator.userAgent) ? "android" : "web";
      await suscribirPush(sub, {
        plataforma,
        user_agent: navigator.userAgent,
      });

      const backend = await estadoPush();
      const activoBackend = Boolean(backend?.data?.activo);
      setPushPermission("granted");
      setPushActive(activoBackend);
      if (!activoBackend) {
        setPushError("No se pudo registrar la suscripcion en el backend.");
        return;
      }
      present({ message: "Notificaciones activadas.", duration: 4000, color: "success" });
    } catch (error: any) {
      const backendMsg = error?.response?.data?.error;
      setPushError(backendMsg || "No se pudieron activar las notificaciones.");
    } finally {
      setPushLoading(false);
    }
  };

  const desactivarNotificaciones = async () => {
    if (pushLoading) return;
    setPushError("");

    if (!soportaPush()) {
      present({ message: "Este dispositivo no soporta notificaciones push.", duration: 4000, color: "warning" });
      return;
    }

    setPushLoading(true);
    try {
      const sub = await obtenerSuscripcionPush();
      if (sub) {
        await desuscribirPush(sub);
        await sub.unsubscribe();
      }
      setPushActive(false);
      setPushPermission(Notification.permission);
      present({ message: "Notificaciones desactivadas.", duration: 4000, color: "success" });
    } catch (error: any) {
      const backendMsg = error?.response?.data?.error;
      setPushError(backendMsg || "No se pudieron desactivar las notificaciones.");
    } finally {
      setPushLoading(false);
    }
  };

  const estadoTextoPush = () => {
    if (!pushSupported) return "No disponible en este navegador";
    if (pushPermission === "denied") return "Permiso denegado en el dispositivo";
    if (pushActive) return "Notificaciones activas";
    return "Notificaciones desactivadas";
  };

  return {
    pushSupported,
    pushPermission,
    pushActive,
    pushLoading,
    pushError,
    refrescarEstadoPush,
    activarNotificaciones,
    desactivarNotificaciones,
    estadoTextoPush,
  };
};
